import React, { useCallback, useContext } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { PasscodeVerificationBlocker } from '@cash-web/protos/squareup/franklin/blockers/passcode_blockers.pb'
import { PasscodeVerificationBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { optionalScopeHeaders } from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationVerifyPasscode } from '@cash-web/shared-data-access-api/react-query/useMutationVerifyPasscode'
// eslint-disable-next-line @nx/workspace-no-new-franklin-get-profile -- pre-existing usage; migrate per #cash-profile-migration
import { useQueryGetProfile } from '@cash-web/shared-data-access-api/react-query/useQueryGetProfile'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { getVerifyPasscodeErrors } from '../../../util/statusErrors'
import { BaseBlockerFormFields, BlockerProps } from '../../'
import { BlockerPasscodeEntry } from '../BlockerPasscodeCreation/BlockerPasscodeEntry'
import { cardBrandMap } from './cardBrandMap'
import { getPasscodeVerificationLength } from './getPasscodeLength'

type FormFields = BaseBlockerFormFields & {
  passcode: string
}

interface Props extends BlockerProps {
  blocker: PasscodeVerificationBlocker
  supplement?: PasscodeVerificationBlockerSupplement
}

export function BlockerPasscodeVerification({ blocker, supplement, skippable }: Props): React.ReactElement {
  const flow = useFlowContext()
  const { t } = useTranslation()
  const { clearError } = useContext(ErrorContext)
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()

  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const { mutateAsync: verifyPasscode } = useMutationVerifyPasscode({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
      optionalRequestHeaders: optionalScopeHeaders(),
    },
    performOptimisticUpdate: setQueryData => {
      // Don't perform optimistic update if we're onboarding
      if (flow.active?.clientScenario === ClientScenario.ONBOARDING) {
        return
      }
      setQueryData(useQueryGetProfile, ({ queryData }) => {
        return {
          ...queryData,
          profile: {
            ...queryData.profile,
            require_passcode_confirmation: true,
          },
        }
      })
    },
  })

  const form = useForm<FormFields>({ mode: 'onTouched' })
  const { setValue, reset, setError } = form
  const passCodeName = 'passcode'

  const passcodeLength: number = getPasscodeVerificationLength(blocker)

  const onSubmit = useCallback(
    async ({ passcode }: FormFields): Promise<void> => {
      await handleBlockerResponse({
        promisedBlockerUpdate: verifyPasscode({
          passcode: passcode,
          // The backend looks as possible values set on the request context, so an empty object is requried at least
          request_context: {},
        }),
        errors: getVerifyPasscodeErrors(genericError, t),
        onFailure: () => {
          if (isArcadeMigrationEnabled) {
            reset({ [passCodeName]: '' })
            // The blocker banner owns the message; the field error starts Arcade's native shake,
            // which restores focus to the first digit when it ends.
            setError(passCodeName, { type: 'server' })
          } else {
            setValue(passCodeName, '', { shouldValidate: true })
          }
        },
      })
    },
    [handleBlockerResponse, verifyPasscode, genericError, t, isArcadeMigrationEnabled, reset, setValue, setError]
  )

  function getHeaderFromBlocker(): string {
    const isCustomerPasscode: boolean =
      blocker.instrument?.token === 'CUSTOMER_PASSCODE' || blocker.instrument?.token === 'CASH_BALANCE'
    const isAmericanExpress: boolean = (blocker.brand || blocker.instrument?.card_brand) === 'AMERICAN_EXPRESS'

    if (isCustomerPasscode) {
      return t('Enter your PIN', { context: 'title' })
    }

    const i18nParams = {
      cardBrand: blocker.brand ? cardBrandMap[blocker.brand] : cardBrandMap.UNKNOWN,
      panSuffix: blocker.pan_suffix || blocker.instrument?.suffix,
    }

    return isAmericanExpress
      ? t('Enter the CVV on the front of your {cardBrand} card ending in {panSuffix}', {
          context: 'title',
          ...i18nParams,
        })
      : t('Enter the CVV on the back of your {cardBrand} card ending in {panSuffix}', {
          context: 'title',
          ...i18nParams,
        })
  }

  const title = supplement?.main_text || getHeaderFromBlocker()

  let helpModal
  if (supplement?.additional_help_items) {
    helpModal = {
      helpItems: supplement?.additional_help_items,
    }
  }

  return (
    <BlockerPasscodeEntry
      name={passCodeName}
      mainText={title}
      secondaryText={undefined} // No subtitle because we don't yet have a generic one that works with all flows that have this blocker.
      form={form}
      length={passcodeLength}
      onSubmit={() => {
        // If a user gets a form level error and then inputs the correct PIN,
        // the form error remains and shows up in the next blocker too. Normally, this doesn't
        // happen since BaseBlocker clears form level errors via form submit event triggering
        // its onSubmit handler. However, here we call handleSubmit directly, which means
        // BaseBlocker doesn't clear the error
        clearError()
        form.handleSubmit(onSubmit)()
      }}
      skippable={skippable}
      helpModal={helpModal}
    />
  )
}
